import _ from "lodash";
/**--external-- */
import React, { useRef, useImperativeHandle } from "react";
import { connect } from "react-redux";
import propTypes from "prop-types";
import classNames from "classnames";

/**--internal-- */
import { useI18n } from "Hooks";
import {FormTextInput} from "UIComponents/InputTextField";
import {FormSelectDropdown} from "UIComponents/SelectDropdown";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import { LANGUAGE_OPTIONS, ROASTER_OPTIONS, DRIVE_OPTIONS } from "Constants";
import ACLStore from "lib/aclStore";
import { REGION_OPTIONS } from "Platform/routes/Regions/modules/Module";
import { setPortalType } from "Login/modules/LoginModule";

/**--relative-- */
import classes from "./Step1.scss";
import DropZoneButtonComponent from "../DropZoneButtonComponent";
import { Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined } from "@toddle-edu/ds-icons";

const styles = {
  overlayInnerStyle: {
    maxWidth: "250px",
    textAlign: "center",
  },
};

const Step1 = props => {
  const {
    name,
    code,
    locale,
    rosteringMethod,
    storageType,
    attachments,
    updateOrganizationInformation,
    customRef,
    setPortalType,
    portalType,
    isAccessManagerV2,
  } = props;

  const { t } = useI18n(["onBoarding"]);

  const nameRef = useRef();
  const languageRef = useRef();
  const rosteringMethodRef = useRef();
  const regionRef = useRef();
  const driveRef = useRef();

  useImperativeHandle(customRef, () => {
    return {
      isValid: () => {
        const refList = [nameRef, languageRef, rosteringMethodRef, driveRef];

        refList.push(regionRef);

        const errorCount = _.reduce(
          refList,
          (result, ref) => {
            return result + (ref?.current?.isValid() ?? 0);
          },
          0
        );

        return errorCount == 0;
      },
    };
  });

  const headerClasses = classNames("heading-3", classes.header);

  const schoolDetailsHeaderClasses = classNames(
    "heading-5",
    classes.schoolDetailsHeader
  );

  const schoolLogoLabelClasses = classNames(
    "text-label-2",
    classes.schoolLogoLabel
  );

  const driveSelectClasses = classNames("text-label-2", classes.driveSelect);

  const logoAttachment = !_.isEmpty(attachments) ? attachments[0] : {};

  const handleRegionChange = region => {
    setPortalType(region);
  };

  return (
    <div className={classes.container}>
      <h3 className={headerClasses}>
        {isAccessManagerV2
          ? t("onBoarding:onboard_new_account")
          : t("onBoarding:onboard_new_school")}
      </h3>
      <h5 className={schoolDetailsHeaderClasses}>
        {isAccessManagerV2
          ? t("onBoarding:account_details_title")
          : t("onBoarding:school_details")}
      </h5>
      <div className={classes.schoolLogoContainer}>
        <p className={schoolLogoLabelClasses}>
          {isAccessManagerV2
            ? t("onBoarding:account_logo")
            : t("onBoarding:school_logo")}
        </p>
        <DropzoneComponent
          accept="image/jpeg,image/png,image/jpg,image/heic,image/heif"
          onContentUploaded={attachments => {
            updateOrganizationInformation({
              key: "attachments",
              value: attachments,
            });
          }}
          multiple={false}
          renderComponent={
            <DropZoneButtonComponent logoAttachment={logoAttachment} />
          }
        />
      </div>
      <div className={classes.schoolDetailsContainer}>
        <FormTextInput
          value={name}
          updateInputField={name =>
            updateOrganizationInformation({ key: "name", value: name })
          }
          placeholder={
            isAccessManagerV2
              ? t("onBoarding:enter_account_name")
              : t("onBoarding:enter_school_name")
          }
          size={"x-large"}
          isRequired
          ref={nameRef}
          label={
            isAccessManagerV2
              ? t("onBoarding:account_name")
              : t("onBoarding:school_name")
          }
        />
        <FormTextInput
          value={code}
          updateInputField={code =>
            updateOrganizationInformation({ key: "code", value: code })
          }
          placeholder={
            isAccessManagerV2
              ? t("onBoarding:enter_account_code")
              : t("onBoarding:enter_school_code")
          }
          size={"x-large"}
          label={
            isAccessManagerV2
              ? t("onBoarding:account_code_optional")
              : t("onBoarding:school_code_optional")
          }
        />
        <div className={classes.selectDropdownContainer}>
          <FormSelectDropdown
            placeholder={t("onBoarding:choose_language")}
            options={LANGUAGE_OPTIONS}
            size={"x-large"}
            value={locale}
            isRequired
            label={t("onBoarding:platform_language")}
            updateInputField={locale =>
              updateOrganizationInformation({
                key: "locale",
                value: locale,
              })
            }
            ref={languageRef}
            menuPlacement="top"
          />
        </div>
        <div className={classes.selectDropdownContainer}>
          <FormSelectDropdown
            placeholder={t("onBoarding:choose_rostering_method")}
            options={ROASTER_OPTIONS}
            size={"x-large"}
            value={rosteringMethod}
            isRequired
            label={t("onBoarding:rostering_method")}
            updateInputField={rosteringMethod =>
              updateOrganizationInformation({
                key: "rosteringMethod",
                value: rosteringMethod,
              })
            }
            ref={rosteringMethodRef}
            menuPlacement="top"
          />
        </div>

        <div className={classes.selectDropdownContainer}>
          <FormSelectDropdown
            placeholder={
              isAccessManagerV2
                ? t("onBoarding:account_region")
                : t("onBoarding:school_region")
            }
            options={REGION_OPTIONS}
            size={"x-large"}
            isRequired
            label={
              isAccessManagerV2
                ? t("onBoarding:account_region")
                : t("onBoarding:school_region")
            }
            value={portalType}
            updateInputField={handleRegionChange}
            ref={regionRef}
            menuPlacement="top"
          />
        </div>
        {ACLStore.can("FeatureFlag:EnableCloudOnboardingDriveSwitch") ? (
          <div className={classes.selectDropdownContainer}>
            <FormSelectDropdown
              placeholder={t("onBoarding:drive_option")}
              options={DRIVE_OPTIONS}
              size={"x-large"}
              isRequired
              labelComponent={
                <div className={driveSelectClasses}>
                  <span>{t("onBoarding:school_cloud_storage_account")}*</span>
                  <Tooltip
                    tooltip={t(
                      "onBoarding:school_cloud_storage_account_tooltip_info"
                    )}
                    overlayInnerStyle={styles.overlayInnerStyle}
                    placement={"bottom"}
                  >
                    <InformationOutlined size={"xx-small"} />
                  </Tooltip>
                </div>
              }
              value={storageType}
              updateInputField={storageType => {
                updateOrganizationInformation({
                  key: "storageType",
                  value: storageType,
                });
              }}
              ref={driveRef}
              menuPlacement="top"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

Step1.propTypes = {
  name: propTypes.string,
  code: propTypes.string,
  locale: propTypes.string,
  rosteringMethod: propTypes.string,
  storageType: propTypes.string,
  logoAttachment: propTypes.object,
  updateOrganizationInformation: propTypes.func,
  customRef: propTypes.object,
};

const mapActionCreators = {
  setPortalType,
};
const mapStateToProps = (state, ownProps) => {
  return {
    portalType: state.login.portalType,
  };
};

export default connect(mapStateToProps, mapActionCreators)(Step1);
